import GeneralError from '@/components/Errors/General';
import PageGray from '@/components/General/PageGray';
import Loader from '@/components/Loader/Loader';
import RankingBox from '@/components/Ranking/RankingBox';
import RankingTabs from '@/components/Graduatorie/RankingTabs';
import H1 from '@/components/Typography/Headings/H1';
import P from '@/components/Typography/P';
import { useGetCollection } from '@/hooks/collections/useCollections';
import { formatDate } from '@/lib/utills';
import { useGetRankings } from '@/hooks/rankings/useRanking';
import { useGetAttempt } from '@/hooks/attempts/useAttempts';
import {
  createFileRoute,
  Link,
  type SearchSchemaInput,
} from '@tanstack/react-router';
import { Icon } from '@testbusters/icon';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_sidebar/graduatorie/$id')({
  validateSearch: (search: { attemptId?: string } & SearchSchemaInput) => ({
    attemptId: search.attemptId ?? '',
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { attemptId } = Route.useSearch();
  const {
    collection,
    isFetching: isCollectionFetching,
    error: isCollectionError,
  } = useGetCollection(id);
  const {
    rankingMap,
    isFetching: isRankingFetching,
    error: isRankingError,
  } = useGetRankings([id]);
  const { attempt, isFetching: isAttemptFetching } = useGetAttempt(attemptId);

  if (isCollectionFetching || isRankingFetching || isAttemptFetching) {
    return <Loader fullScreen />;
  }

  if (isCollectionError || isRankingError) {
    return <GeneralError />;
  }

  const ranking = rankingMap.get(id);

  return (
    <PageGray className="flex flex-col gap-8">
      <div className="flex gap-2">
       <Link to='/graduatorie'>
        <Icon
          name="chevron-left"
          className="text-3xl mt-4"
        />
       </Link>
        <div className="flex flex-col gap-4">
          <H1>{collection?.name}</H1>
          <P>{t('graduatorie.detail.subtitle')}</P>
        </div>
      </div>
      <div className="grid md:grid-cols-4 grid-cols-2 gap-6">
        <RankingBox
          label={t('graduatorie.detail.boxes.position')}
          value={ranking?.myPosition.toString()}
          icon="trophy"
        />
        <RankingBox
          label={t('graduatorie.detail.boxes.participants')}
          value={ranking?.total.toString()}
          icon="group"
        />
        <RankingBox
          label={t('graduatorie.detail.boxes.percentile')}
          value={ranking?.myPercentile.toString()}
          icon="percentage"
        />
        <RankingBox
          label={t('graduatorie.detail.boxes.deliveryDate')}
          value={formatDate(attempt?.updatedAt?.toString()!)}
          icon="calendar"
        />
      </div>

      {ranking && (
        <RankingTabs
          ranking={ranking}
          collection={collection!}
        />
      )}
    </PageGray>
  );
}
